import { Plus, Search } from "lucide-react";
import { Link } from "react-router-dom";
import { buttonVariants } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { Table, TableWrapper } from "@/components/ui/table";
import { WIP_ACTION_HINT, WipHint } from "@/components/ui/wip-hint";
import { FilterBar } from "@/components/data-display/filter-bar";
import { PageHeader } from "@/components/data-display/page-header";
import { TableState } from "@/components/data-display/table-state";
import { UnavailableValue } from "@/components/data-display/unavailable-value";
import { cn } from "@/lib/utils";

/**
 * Governance overview — layout port of the uat prototype
 * (`.design-ref/prototype/app/(enterprise-wallet)/governance/page.tsx` and
 * `features/oversight/governance.tsx#GovernanceOverview`): page header with its
 * "Create Policy" action, the four policy stat tiles, the filter bar, and the
 * policy table with its five canonical columns.
 *
 * Policy definitions and evaluations have no backend route in this environment
 * (page-delivery-pipeline.md G6), so the prototype's oversight-data.ts fixtures
 * (POLICY_STATS totals, category groups, POL- id rows, wallet counts, evaluation
 * timestamps) are deliberately NOT rendered: every stat tile shows the shared
 * unavailable marker, the filter controls are disabled, and the table shows its
 * designed empty state. Nothing on this page describes a real policy or a real
 * decision.
 *
 * The "Create Policy" action links to `/governance/new`, the designed
 * policy-authoring layout (which is itself a WIP screen), so it is a real
 * navigation rather than a dead link. The WipHint stays beside it because the
 * destination has no backend yet.
 */

const WIP_ACTION_HINT_ID = "governance-wip-action-hint";

const COLUMNS = [
  { key: "id", label: "ID" },
  { key: "name", label: "Name" },
  { key: "status", label: "Status" },
  { key: "wallets", label: "Wallets" },
  { key: "lastEvaluated", label: "Last Evaluated" },
] as const;

/** The prototype's four stat tiles, with no value the backend does not hold. */
const STAT_LABELS = ["Total Policies", "Active", "Draft", "Evaluations Today"] as const;

export default function GovernancePage() {
  return (
    <div>
      {/* Reason shared by every disabled control below, referenced via aria-describedby. */}
      <span id={WIP_ACTION_HINT_ID} className="sr-only">
        {WIP_ACTION_HINT}
      </span>

      {/* `flex-wrap` so a second action wraps under the first instead of
          forcing the header row wider than a 375px viewport. The action
          carries `min-h-[36px]` because the Button's `sm` size is 32px tall
          and this lane's tap-target floor is 36px. */}
      <PageHeader
        title="Governance"
        subtitle="Transfer policies and their evaluations."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <WipHint />
            <Link
              to="/governance/new"
              className={cn(buttonVariants({ variant: "primary", size: "sm" }), "min-h-[36px]")}
            >
              <Plus className="size-4" aria-hidden /> Create Policy
            </Link>
          </div>
        }
      />

      <p className="mb-5 text-sm text-muted">
        Governance has no backend in this environment yet, so no policy, wallet count or evaluation
        result is listed.
      </p>

      {/* One column on a phone (375px), two from sm, four from lg: the value
          slot holds the words "Not available yet", and at the desktop 28px
          scale that phrase is wider than a half-width phone tile, so a
          `grid-cols-2` base pushed the page wider than the viewport. */}
      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {STAT_LABELS.map((label) => (
          // Same tile shell as `StatCard`, but the value slot carries the shared
          // unavailable marker: an unknown total is never rendered as a number.
          <div key={label} className="rounded-lg border border-line bg-surface px-5 py-[18px]">
            <div className="text-[20px] font-bold leading-[1.1] tracking-tight sm:text-[28px]">
              <UnavailableValue />
            </div>
            <div className="mt-1 text-xs text-muted">{label}</div>
          </div>
        ))}
      </div>

      <FilterBar className="mb-5">
        <div className="relative flex-1 min-w-[200px] max-w-[300px]">
          <Search
            aria-hidden
            className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted"
          />
          <input
            type="text"
            defaultValue=""
            disabled
            placeholder="Search policies by ID, name or category"
            aria-label="Search policies"
            title={WIP_ACTION_HINT}
            aria-describedby={WIP_ACTION_HINT_ID}
            className="w-full py-[7px] pl-9 pr-2 text-sm text-ink outline-none placeholder:text-subtle disabled:cursor-not-allowed"
          />
        </div>
        <Select
          disabled
          defaultValue=""
          aria-label="Filter by category"
          title={WIP_ACTION_HINT}
          aria-describedby={WIP_ACTION_HINT_ID}
        >
          <option value="">All Categories</option>
        </Select>
        <Select
          disabled
          defaultValue=""
          aria-label="Filter by status"
          title={WIP_ACTION_HINT}
          aria-describedby={WIP_ACTION_HINT_ID}
        >
          <option value="">All Statuses</option>
        </Select>
      </FilterBar>

      <TableWrapper>
        <Table className="dense">
          <thead>
            <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
              {COLUMNS.map((column) => (
                <th key={column.key} scope="col" className="px-4 py-2.5">
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <TableState
              variant="empty"
              colSpan={COLUMNS.length}
              title="Governance is not available in this environment yet"
              description="Policy definitions and evaluations have no backend route in this environment yet. Nothing on this page reflects a real policy or decision."
            />
          </tbody>
        </Table>
      </TableWrapper>
    </div>
  );
}
